import * as React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useStore } from 'react-admin';

import { getAccessToken, getIdentityCached } from '../api/tokenStorage';
import { ACTIVE_APP_STORE_KEY } from './activeAppStore';
import { DEFAULT_ACTIVE_APP_ID, workspaceRootPath } from './appsRegistry';
import { useTenantApps } from './tenantApps';

/**
 * Startup redirect from `/`.
 *
 * Priority for the target app:
 * 1. The in-session active app (stored per-tab) — if it's installed and was explicitly set
 *    this session (not just the initial default).
 * 2. The resolver's `defaultStartupAppId` — lastUsedAppId → group/company default → first installed.
 * 3. `/select-app` when nothing can be determined.
 */
export function RootRedirect() {
    const [activeAppId] = useStore<string>(ACTIVE_APP_STORE_KEY, DEFAULT_ACTIVE_APP_ID);
    const { installedAppIds, defaultStartupAppId, isLoaded } = useTenantApps();
    const identity = getIdentityCached();

    // Wait for the live API data before making routing decisions.
    // This prevents a white flash and stops premature redirects based on stale cache.
    if (!isLoaded) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // No token after load = session expired or never existed → go to login.
    if (!getAccessToken()) {
        return <Navigate replace to="/login" />;
    }

    if (identity?.companyLicenseLocked) {
        return <Navigate replace to="/license-blocked" />;
    }

    if (installedAppIds.length === 0) {
        return <Navigate replace to="/select-app" />;
    }

    // 1. Explicit in-session selection (set by OdooAppSwitcher / SelectApp via sessionStorage).
    //    sessionStorage clears when the tab is closed, so this only persists within a single tab session.
    const sessionStoredApp = sessionStorage.getItem('sessionActiveAppId');
    const sessionApp =
        sessionStoredApp && installedAppIds.includes(sessionStoredApp) ? sessionStoredApp :
        // Fall back: the RA store if it differs from the initial default (user navigated in this tab).
        activeAppId !== DEFAULT_ACTIVE_APP_ID && installedAppIds.includes(activeAppId) ? activeAppId :
        undefined;

    const targetAppId = sessionApp ?? defaultStartupAppId;

    if (!targetAppId || !installedAppIds.includes(targetAppId)) {
        return <Navigate replace to="/select-app" />;
    }

    return <Navigate replace to={workspaceRootPath(targetAppId)} />;
}

export default RootRedirect;
